/**
 * NOVO CAMPO: o formulário que faz um Campo existir, com cadastro dentro.
 *
 * É a maior escrita que este sistema faz de uma vez. Ao apertar o botão, o servidor origina as
 * congregações, os departamentos, as onze linhas do Fluxo, as configurações do papel, as pessoas
 * e a grade de permissões de cada uma, tudo como evento, e manda a cada pessoa o link para ela
 * criar a senha dela.
 *
 * ## As quatro coisas que este formulário cuida, e que não são enfeite
 *
 * **1. O limite de aprovação é obrigatório para quem não é Pastor do Campo**, e a tela não sugere
 * número nenhum. Vazio quer dizer **sem limite**, e o valor de fábrica da coluna saiu numa
 * migration do notebook: deixar em branco por esquecimento faria a pessoa aprovar qualquer
 * quantia. O Pastor do Campo é o único que nasce sem limite, e nele o campo nem aparece.
 *
 * **2. Exatamente uma congregação é a sede.** Ela é a congregação do Campo, de onde sai o papel.
 * Nenhuma ou duas fariam o cadastro nascer torto, e o servidor recusa.
 *
 * **3. A pessoa nasce sem senha que funcione.** Não existe senha provisória neste caminho: ela
 * recebe um link de 48 horas, escolhe a senha no navegador dela, e só então o hash viaja para os
 * notebooks do Campo. É por isso que o e-mail é obrigatório aqui.
 *
 * **4. O pedido é sorteado quando o formulário abre.** Apertar duas vezes, ou perder a internet
 * no meio e tentar de novo, devolve o MESMO Campo em vez de criar dois. Quem faz isso é o
 * servidor, pelo `pedido_id`; esta tela só não pode trocar o número no meio do caminho.
 */
import { useMemo, useState, type FormEvent, type JSX } from 'react'
import { ehSessaoTerminada, ErroDoServidor } from '../../api'
import {
  administracao, CARGOS, type CampoCriado, type CargoDaTela, type PedidoDeCampoNovo
} from '../../api-da-administracao'

interface CongregacaoNoFormulario {
  /** Só para o React saber qual linha é qual. Não vai para o servidor. */
  chave: number
  nome: string
}

interface PessoaNoFormulario {
  chave: number
  nome: string
  email: string
  cargo: CargoDaTela
  /** O NOME da congregação, porque os identificadores só existem depois de o Campo nascer. */
  congregacao: string
  /** Em reais, como a pessoa digita. Virá centavos na hora de mandar. */
  limite: string
}

/** O número do pedido, sorteado uma vez por formulário aberto. */
function sortearOPedido(): string {
  const sorteio =
    globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`
  return `tela-${sorteio}`
}

/** `1.234,50` e `1234.50` viram 123450. Nulo quando não dá para ler um número. */
export function emCentavos(digitado: string): number | null {
  const limpo = digitado.trim().replace(/[^\d.,]/g, '')
  if (limpo === '') return null

  /*
   * Quem digita dinheiro no Brasil usa vírgula, e às vezes ponto de milhar. Então o último
   * separador é o dos centavos, e os outros são de milhar. É a mesma leitura do notebook.
   */
  const ultimo = Math.max(limpo.lastIndexOf(','), limpo.lastIndexOf('.'))
  const inteiros = (ultimo === -1 ? limpo : limpo.slice(0, ultimo)).replace(/[.,]/g, '')
  const centavos = ultimo === -1 ? '' : limpo.slice(ultimo + 1).replace(/[.,]/g, '')

  const numero = Number(`${inteiros || '0'}.${(centavos + '00').slice(0, 2)}`)
  return Number.isFinite(numero) ? Math.round(numero * 100) : null
}

export function CampoNovo({
  aoCriar,
  aoDesistir,
  aoSair
}: {
  aoCriar: (feito: CampoCriado) => void
  aoDesistir: () => void
  aoSair: () => void
}): JSX.Element {
  const pedidoId = useMemo(sortearOPedido, [])

  const [nome, setNome] = useState('')
  const [sede, setSede] = useState('')
  const [timbre, setTimbre] = useState(['', '', ''])
  const [outras, setOutras] = useState<CongregacaoNoFormulario[]>([])
  const [pessoas, setPessoas] = useState<PessoaNoFormulario[]>([
    { chave: 1, nome: '', email: '', cargo: 'pastor_do_campo', congregacao: '', limite: '' }
  ])
  const [erro, setErro] = useState<string | null>(null)
  const [indo, setIndo] = useState(false)

  /** Os nomes que as pessoas podem escolher: a sede e as outras, como estão digitadas agora. */
  const congregacoes = [sede.trim(), ...outras.map((c) => c.nome.trim())].filter((n) => n !== '')

  function mexerNaPessoa(chave: number, o: Partial<PessoaNoFormulario>): void {
    setPessoas((antes) => antes.map((p) => (p.chave === chave ? { ...p, ...o } : p)))
  }

  function oQueFalta(): string | null {
    if (nome.trim() === '') return 'Falta o nome do Campo.'
    if (sede.trim() === '') return 'Falta o nome da congregação sede.'
    if (pessoas.length === 0) return 'Falta pelo menos uma pessoa.'

    const repetida = congregacoes.find((c, i) => congregacoes.indexOf(c) !== i)
    if (repetida !== undefined) {
      return `Duas congregações com o nome "${repetida}". Cada uma precisa de um nome próprio.`
    }

    for (const p of pessoas) {
      if (p.nome.trim() === '') return 'Falta o nome de uma das pessoas.'
      if (p.email.trim() === '') {
        return `Falta o e-mail de ${p.nome.trim()}. É por ele que ela recebe o link de criar a senha.`
      }
      if (p.congregacao === '') return `Falta dizer de qual congregação é ${p.nome.trim()}.`

      const cargo = CARGOS.find((c) => c.valor === p.cargo)!
      if (!cargo.semLimite && !cargo.limiteZero && emCentavos(p.limite) === null) {
        return (
          `Falta o limite de aprovação de ${p.nome.trim()}. Deixar em branco quer dizer SEM ` +
          'LIMITE, e só o Pastor do Campo nasce assim.'
        )
      }
    }

    return null
  }

  async function criar(evento: FormEvent): Promise<void> {
    evento.preventDefault()

    const falta = oQueFalta()
    if (falta !== null) return setErro(falta)

    setErro(null)
    setIndo(true)

    const pedido: PedidoDeCampoNovo = {
      pedido_id: pedidoId,
      nome: nome.trim(),
      congregacoes: [
        { nome: sede.trim(), e_sede: true },
        ...outras
          .map((c) => c.nome.trim())
          .filter((n) => n !== '')
          .map((n) => ({ nome: n, e_sede: false }))
      ],
      pessoas: pessoas.map((p) => ({
        nome: p.nome.trim(),
        email: p.email.trim(),
        cargo: p.cargo,
        congregacao: p.congregacao,
        /*
         * O Pastor do Campo vai com vazio de propósito: vazio é sem limite. O Tesoureiro vai com
         * zero, e o servidor confere de novo: é ele quem decide.
         */
        limite_aprovacao: CARGOS.find((c) => c.valor === p.cargo)!.semLimite
          ? null
          : CARGOS.find((c) => c.valor === p.cargo)!.limiteZero
            ? 0
            : emCentavos(p.limite)
      }))
    }

    const linhasDoTimbre = timbre.map((l) => l.trim()).filter((l) => l !== '')
    if (linhasDoTimbre.length > 0) pedido.timbre = linhasDoTimbre

    try {
      aoCriar(await administracao.criarCampo(pedido))
    } catch (falha) {
      if (ehSessaoTerminada(falha)) return aoSair()
      setErro(falha instanceof ErroDoServidor ? falha.message : 'Não foi possível criar o Campo.')
      setIndo(false)
    }
  }

  return (
    <form className="campo-novo" onSubmit={(e) => void criar(e)}>
      <h2>Um Campo novo</h2>

      <p className="explicacao">
        Ao criar, o servidor monta o cadastro inteiro deste Campo e manda a cada pessoa um link
        para ela escolher a senha dela. <strong>Ninguém nasce com senha aqui.</strong>
      </p>

      <label>
        Nome do Campo
        <input
          className="campo"
          value={nome}
          onChange={(e) => setNome(e.target.value)}
          placeholder="Campo de Guarulhos"
          maxLength={120}
          required
        />
      </label>

      <fieldset>
        <legend>Congregações</legend>

        <label>
          A sede
          <input
            className="campo"
            value={sede}
            onChange={(e) => setSede(e.target.value)}
            placeholder="Igreja Central"
            maxLength={120}
            required
          />
          <span className="ajuda-do-codigo">
            É a congregação do Campo, de onde sai o papel. Exatamente uma é a sede.
          </span>
        </label>

        {outras.map((c, i) => (
          <label key={c.chave}>
            Congregação {i + 2}
            <span className="linha-de-congregacao">
              <input
                className="campo"
                value={c.nome}
                onChange={(e) =>
                  setOutras((antes) =>
                    antes.map((x) => (x.chave === c.chave ? { ...x, nome: e.target.value } : x))
                  )
                }
                placeholder="Vila das Flores"
                maxLength={120}
              />
              <button
                type="button"
                className="mini"
                onClick={() => setOutras((antes) => antes.filter((x) => x.chave !== c.chave))}
              >
                Tirar
              </button>
            </span>
          </label>
        ))}

        <div className="linha-form">
          <button
            type="button"
            className="mini"
            onClick={() => setOutras((antes) => [...antes, { chave: Date.now(), nome: '' }])}
          >
            Acrescentar congregação
          </button>
        </div>
      </fieldset>

      <fieldset>
        <legend>Pessoas</legend>

        {pessoas.map((p, i) => {
          const cargo = CARGOS.find((c) => c.valor === p.cargo)!

          return (
            <div className="pessoa-nova" key={p.chave}>
              <p className="titulo-da-pessoa">
                Pessoa {i + 1}
                {pessoas.length > 1 && (
                  <button
                    type="button"
                    className="mini"
                    onClick={() => setPessoas((antes) => antes.filter((x) => x.chave !== p.chave))}
                  >
                    Tirar
                  </button>
                )}
              </p>

              <label>
                Nome
                <input
                  className="campo"
                  value={p.nome}
                  onChange={(e) => mexerNaPessoa(p.chave, { nome: e.target.value })}
                  placeholder="Nome como ele aparece no papel"
                  maxLength={200}
                  required
                />
              </label>

              <label>
                E-mail
                <input
                  className="campo"
                  type="email"
                  value={p.email}
                  onChange={(e) => mexerNaPessoa(p.chave, { email: e.target.value })}
                  autoCapitalize="none"
                  spellCheck={false}
                  maxLength={200}
                  required
                />
                <span className="ajuda-do-codigo">
                  É por aqui que ela recebe o link de criar a senha, e ele vale 48 horas.
                </span>
              </label>

              <label>
                Cargo
                {/*
                  O `aria-label` existe porque o rótulo envolve o `select`, e aí o nome que o
                  leitor de tela anuncia junta o rótulo com as opções de dentro ("Cargo Pastor do
                  Campo Tesoureiro do Campo..."). Com o número da pessoa dentro dele, cada caixa
                  desta lista passa a ter nome próprio, e é por esse nome que a prova do navegador
                  a encontra.
                */}
                <select
                  className="campo"
                  aria-label={`Cargo da pessoa ${i + 1}`}
                  value={p.cargo}
                  onChange={(e) =>
                    mexerNaPessoa(p.chave, {
                      cargo: e.target.value as PessoaNoFormulario['cargo'],
                      /* Trocando para Pastor do Campo, o limite escrito deixa de valer. */
                      limite: ''
                    })
                  }
                >
                  {CARGOS.map((c) => (
                    <option key={c.valor} value={c.valor}>{c.titulo}</option>
                  ))}
                </select>
                <span className="ajuda-do-codigo">{cargo.explicacao}</span>
              </label>

              <label>
                Congregação
                <select
                  className="campo"
                  aria-label={`Congregação da pessoa ${i + 1}`}
                  value={p.congregacao}
                  onChange={(e) => mexerNaPessoa(p.chave, { congregacao: e.target.value })}
                  required
                >
                  <option value="">escolha</option>
                  {congregacoes.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </label>

              {cargo.semLimite ? (
                <p className="explicacao">
                  O Pastor do Campo aprova <strong>sem limite</strong>, e é o único que nasce
                  assim. Ele também é o administrador máximo deste Campo.
                </p>
              ) : cargo.limiteZero ? (
                <p className="explicacao">
                  O Tesoureiro <strong>não aprova nada</strong>: o limite dele nasce em zero, e
                  quem decide mudar isso é o Campo, no programa da sede.
                </p>
              ) : (
                <label>
                  Limite de aprovação, em reais
                  <input
                    className="campo"
                    value={p.limite}
                    onChange={(e) => mexerNaPessoa(p.chave, { limite: e.target.value })}
                    inputMode="decimal"
                    placeholder="500,00"
                    maxLength={14}
                    /*
                      SEM `required`, de propósito, e é a única caixa obrigatória deste
                      formulário que não o tem.
                      O aviso do navegador diz "preencha este campo", e aqui isso é pouco: o
                      perigo é justamente a pessoa achar que vazio é zero, quando vazio quer
                      dizer SEM LIMITE. Quem confere este campo é a tela, logo abaixo, com a
                      frase que explica o porquê.
                    */
                  />
                  <span className="ajuda-do-codigo">
                    Quanto ela pode aprovar de uma vez. <strong>Em branco quer dizer sem
                    limite</strong>, e por isso este número é obrigatório. Zero quer dizer que ela
                    não aprova nada.
                  </span>
                </label>
              )}
            </div>
          )
        })}

        <div className="linha-form">
          <button
            type="button"
            className="mini"
            onClick={() =>
              setPessoas((antes) => [
                ...antes,
                {
                  chave: Date.now(),
                  nome: '',
                  email: '',
                  cargo: 'pastor_dirigente',
                  congregacao: '',
                  limite: ''
                }
              ])
            }
          >
            Acrescentar pessoa
          </button>
        </div>
      </fieldset>

      <fieldset>
        <legend>O timbre do papel, se você já souber</legend>

        <p className="explicacao">
          As três linhas que saem no alto do relatório do Campo. Podem ficar em branco: o Campo
          preenche depois, no programa do computador.
        </p>

        {timbre.map((linha, i) => (
          <label key={i}>
            Linha {i + 1}
            <input
              className="campo"
              value={linha}
              onChange={(e) =>
                setTimbre((antes) => antes.map((l, j) => (j === i ? e.target.value : l)))
              }
              maxLength={120}
            />
          </label>
        ))}
      </fieldset>

      {erro !== null && <p className="recado" role="alert">{erro}</p>}

      <div className="linha-form">
        <button className="principal" type="submit" disabled={indo}>
          {indo ? 'Criando o Campo...' : 'Criar o Campo e mandar os links'}
        </button>
        <button type="button" onClick={aoDesistir} disabled={indo}>
          Desistir
        </button>
      </div>
    </form>
  )
}
